import { IRegisterRow } from "./interfaces/IRegisterRow";
import { IStandCycleRotationRow } from "./interfaces/IStandCycleRotationRow";

export type SelectValue = string | string[] | null;

export interface StepAssociateStandCycleRotationData {
  StandCycleRotations: string[];
  Rows: IStandCycleRotationRow[];
}

export interface StepSetPropertyTypeData {
  PropertyType: string | null;
  Rows: IStandCycleRotationRow[];
}

export const PROPERTY_OPTIONS = [
  { key: '1', value: 'Property 001' },
  { key: '2', value: 'Property 002' },
  { key: '3', value: 'Property 003' },
];

export const PROPERTY_TYPE_OPTIONS = [
  { key: '1', value: 'Propia' },
  { key: '2', value: 'Arrendada' },
  { key: '3', value: 'Cesion' },
];

export const STAND_CYCLE_ROTATION_OPTIONS = [
  { key: '1', value: 'Stand 101 - Cycle 1 - Rotation 1' },
  { key: '2', value: 'Stand 204 - Cycle 2 - Rotation 1' },
  { key: '3', value: 'Stand 315 - Cycle 1 - Rotation 2' },
  { key: '4', value: 'Stand 408 - Cycle 3 - Rotation 1' },
];

export const STAND_CYCLE_ROTATION_COLUMN_HEADER_KEYS = {
  stand: 'Planning::Entity.Stand',
  cycle: 'Geographic::Entity.Cycle',
  rotation: 'Geographic::Entity.Rotation',
  area: 'Planning::Entity.Stand.StandArea',
  standType: 'LanguageService::Stand.StandType',
  nearbyPopulation: 'LanguageService::Stand.NearbyPopulation',
  maintenancePeriod: 'LanguageService::Stand.MaintenancePeriod',
  maintenanceReason: 'LanguageService::Stand.MaintenanceReason',
  adjustmentType: 'LanguageService::Stand.AdjustmentType',
  plantationDate: 'Inventory::Entity.CalculationResult.PlantationDate',
  ownPlantation: 'LanguageService::Stand.OwnPlantation',
  spacing: 'LanguageService::Stand.Spacing',
  plantationType: 'LanguageService::Stand.PlantingType',
  geneticMaterial: 'LanguageService::Stand.GeneticMaterial',
  species: 'LanguageService::Stand.Species',
};

export const toSingleSelectValue = (value: SelectValue): string | null => {
  if (Array.isArray(value)) {
    return value[0] ?? null;
  }

  return value || null;
};

export const toMultiSelectValues = (value: SelectValue): string[] => {
  if (Array.isArray(value)) {
    return value;
  }

  if (!value) {
    return [];
  }

  return [value];
};

const DATA_SEPARATOR = '|';

const mapRowValues = <TRow extends object>(
  keys: readonly (keyof TRow)[],
  row: string,
): TRow =>
  Object.fromEntries(
    keys.map((key, index) => [key, row.split(DATA_SEPARATOR)[index] ?? '']),
  ) as TRow;

const mapRowCollection = <TRow extends object>(
  keys: readonly (keyof TRow)[],
  rowsByKey: Record<string, readonly string[]>,
): Record<string, TRow[]> =>
  Object.fromEntries(
    Object.entries(rowsByKey).map(([key, rows]) => [
      key,
      rows.map(row => mapRowValues<TRow>(keys, row)),
    ]),
  ) as Record<string, TRow[]>;

const REGISTER_ROW_KEYS = [
  'id',
  'name',
  'department',
  'judicialSection',
  'documentaryArea',
  'coneatIndex',
  'deedValue',
  'fiscalValue',
  'previousName',
  'description',
  'propertyType',
  'foManagement',
  'lastManagementStart',
] as const satisfies readonly (keyof IRegisterRow)[];

const REGISTER_ROW_VALUES_BY_PROPERTY_ID: Record<string, readonly string[]> = {
  '1': [
    '1-1|10.245|Durazno|7a|1,254.40|138|USD 450,000|UYU 6,850,000|Santa Elena|Padron principal del contrato de tierra.|Propia|Si|2025-01-15',
    '1-2|10.246|Durazno|7a|842.10|121|USD 298,000|UYU 4,125,000|Las Vertientes|Padron complementario con frente operativo activo.|Arrendada|No|2024-09-02',
  ],
  '2': [
    '2-1|22.981|Tacuarembo|3a|2,115.75|97|USD 615,000|UYU 8,940,000|Don Ricardo|Padron con servidumbre de paso registrada.|Propia|Si|2025-03-10',
    '2-2|22.982|Tacuarembo|3a|1,089.00|105|USD 320,000|UYU 4,880,000|El Ombu|Padron asociado al contrato forestal vigente.|Cesion|Si|2024-11-28',
  ],
  '3': [
    '3-1|31.114|Paysandu|12a|965.32|149|USD 287,500|UYU 3,960,000|La Colina|Padron con historial reciente de cambio de tipo de propiedad.|Arrendada|No|2025-02-01',
    '3-2|31.115|Paysandu|12a|1,432.88|132|USD 389,900|UYU 5,410,000|Las Moras|Padron productivo incluido para pruebas de maquetado.|Propia|Si|2024-12-16',
  ],
};

export const REGISTERS_BY_PROPERTY_ID = mapRowCollection<IRegisterRow>(
  REGISTER_ROW_KEYS,
  REGISTER_ROW_VALUES_BY_PROPERTY_ID,
);

const STAND_CYCLE_ROTATION_ROW_KEYS = [
  'key',
  'stand',
  'cycle',
  'rotation',
  'area',
  'standType',
  'nearbyPopulation',
  'maintenancePeriod',
  'maintenanceReason',
  'adjustmentType',
  'plantationDate',
  'ownPlantation',
  'spacing',
  'geneticMaterial',
  'species',
] as const satisfies readonly (keyof IStandCycleRotationRow)[];

const STAND_CYCLE_ROTATION_ROW_VALUES_BY_ID: Record<string, string> = {
  '1': '1|Stand 101|1|1|24.50|Plantation|No|2026-Q1|Scheduled|Area review|2022-07-10|Yes|3 x 2|GM-204|Eucalyptus grandis',
  '2': '2|Stand 204|2|1|18.90|Replanting|Yes|2026-Q2|Nearby population control|Boundary adjustment|2021-09-18|No|3.5 x 2.2|GM-118|Eucalyptus dunnii',
  '3': '3|Stand 315|1|2|32.10|Plantation|No|2026-Q3|Preventive maintenance|Operational adjustment|2023-05-02|Yes|4 x 2.5|GM-330|Eucalyptus globulus',
  '4': '4|Stand 408|3|1|27.40|Recovery|Yes|2026-Q4|Corrective maintenance|Road access adjustment|2020-11-26|No|3 x 2.5|GM-412|Eucalyptus smithii',
};

export const STAND_CYCLE_ROTATION_ROWS_BY_ID: Record<string, IStandCycleRotationRow> =
  Object.fromEntries(
    Object.entries(STAND_CYCLE_ROTATION_ROW_VALUES_BY_ID).map(([key, values]) => [
      key,
      mapRowValues<IStandCycleRotationRow>(STAND_CYCLE_ROTATION_ROW_KEYS, values),
    ]),
  ) as Record<string, IStandCycleRotationRow>;

export const buildStandCycleRotationRows = (selectedIds: string[]): IStandCycleRotationRow[] =>
  selectedIds
    .map(id => STAND_CYCLE_ROTATION_ROWS_BY_ID[id])
    .filter((row): row is IStandCycleRotationRow => !!row);
